import React, { useState, useEffect } from 'react';
import { Plus, Search, Mail, Phone, Building2, User, MapPin, Edit, X } from 'lucide-react';
import toast from 'react-hot-toast';
import AddContactModalComprehensive from './AddContactModalComprehensive';
import ContactDetailModal from './ContactDetailModal';
import EditContactModal from './EditContactModal';
import ViewToggle from './shared/ViewToggle';
import Pagination from './shared/Pagination';

const ContactsManagement = ({ selectedUser, myTeam, teamMembers }) => {
  const [contacts, setContacts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, customer, vendor
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  
  // ViewToggle state for My Contacts / All Contacts
  const [viewMode, setViewMode] = useState('my'); // 'my' or 'all'
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType, viewMode, itemsPerPage]);

  // Fetch contacts
  const fetchContacts = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/contacts`);
      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts || []);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch customers and vendors for dropdowns
  const fetchEntities = async () => {
    try {
      const [customersRes, vendorsRes] = await Promise.all([
        fetch(`${backendUrl}/api/customers`),
        fetch(`${backendUrl}/api/vendors`)
      ]);
      
      if (customersRes.ok) {
        const customerData = await customersRes.json();
        setCustomers(customerData.customers || []);
      }
      
      if (vendorsRes.ok) {
        const vendorData = await vendorsRes.json();
        setVendors(vendorData.vendors || []);
      }
    } catch (error) {
      console.error('Error fetching entities:', error);
    }
  };

  useEffect(() => {
    fetchContacts();
    fetchEntities();
  }, []);

  // Check if a contact belongs to the current user
  // Extract team lead from teamMembers (same pattern as Companies)
  const teamLead = teamMembers && Array.isArray(teamMembers) 
    ? teamMembers.find(member => member.role === 'lead_recruiter' || member.role === 'recruiter_supervisor')
    : null;

  const isMyContact = (contact) => {
    if (!selectedUser?.id || !contact) return false;
    
    // Check if user created this contact
    if (contact.created_by === selectedUser.id || contact.created_by_id === selectedUser.id) {
      return true;
    }
    
    // Check if contact belongs to a company assigned to the user or team
    if (contact.company_id) {
      const company = customers.find(c => c.id === contact.company_id);
      if (company) {
        const companyName = company.company_name || company.client_name || '';
        
        // Check if user is the account manager for this company
        if (company.account_manager_id === selectedUser.id) {
          return true;
        }
        
        // Check if user is in assigned recruiters for this company
        if (company.assigned_recruiters && Array.isArray(company.assigned_recruiters)) {
          if (company.assigned_recruiters.some(ar => ar.recruiter_id === selectedUser.id)) {
            return true;
          }
        }
        
        // Check if user created the company
        if (company.created_by === selectedUser.id) {
          return true;
        }
        
        // Check if company is assigned to team lead's assigned_clients (SAME LOGIC AS COMPANIES)
        if (teamLead?.assigned_clients && Array.isArray(teamLead.assigned_clients)) {
          const isTeamClient = teamLead.assigned_clients.some(clientName => {
            const clientLower = clientName.toLowerCase().trim();
            const companyLower = companyName.toLowerCase().trim();
            
            // Exact match or starts with
            if (companyLower === clientLower || companyLower.startsWith(clientLower + ' ')) {
              return true;
            }
            
            // Check if client name is a significant word at the start
            const companyWords = companyLower.split(/[\s,\-()]+/);
            if (companyWords[0] === clientLower || (companyWords[0] + ' ' + companyWords[1]) === clientLower) {
              return true;
            }
            
            return false;
          });
          if (isTeamClient) {
            return true;
          }
        }
      }
    }
    
    return false;
  };

  // Filter contacts
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.display_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.phone_number?.includes(searchTerm);
    
    const matchesType = 
      filterType === 'all' || 
      contact.contact_type === filterType;
    
    // ViewMode filter - My Contacts vs All Contacts
    const matchesOwnership = viewMode === 'all' || isMyContact(contact);
    
    return matchesSearch && matchesType && matchesOwnership;
  });
  
  // Pagination calculations
  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedContacts = filteredContacts.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Contacts</h2>
          <p className="text-gray-600 mt-1">Manage customer and vendor contacts</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Add Contact
        </button>
      </div>

      {/* View Toggle - My Contacts / All Contacts */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {viewMode === 'my' ? 'My Contacts' : 'All Contacts'} ({filteredContacts.length})
          </h3>
          <ViewToggle 
            leftLabel="My Contacts"
            rightLabel="All Contacts"
            activeView={viewMode}
            onToggle={setViewMode}
            leftValue="my"
            rightValue="all"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search contacts by ID, name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Type Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterType === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterType('customer')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterType === 'customer'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Customers
            </button>
            <button
              onClick={() => setFilterType('vendor')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterType === 'vendor'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Vendors
            </button>
          </div>
        </div>
      </div>

      {/* Contacts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredContacts.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-lg border border-gray-200">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No contacts found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || filterType !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by adding your first contact'}
            </p>
            {!searchTerm && filterType === 'all' && (
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="h-5 w-5" />
                Add Contact
              </button>
            )}
          </div>
        ) : (
          paginatedContacts.map(contact => (
            <div 
              key={contact.id} 
              className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => {
                setSelectedContact(contact);
                setShowDetailModal(true);
              }}
              data-testid={`contact-card-${contact.id}`}
            >
              {/* Header with ID Badge */}
              <div className="mb-3">
                {contact.display_id && (
                  <span className="inline-block px-2 py-1 text-xs font-mono font-semibold bg-blue-100 text-blue-800 rounded mb-2">
                    {contact.display_id}
                  </span>
                )}
              </div>
              
              {/* Name and Type */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {contact.first_name} {contact.last_name}
                  </h3>
                  {contact.title && (
                    <p className="text-sm text-gray-600">{contact.title}</p>
                  )}
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  contact.contact_type === 'customer'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-purple-100 text-purple-700'
                }`}>
                  {contact.contact_type === 'customer' ? 'Customer' : 'Vendor'}
                </span>
              </div>

              {/* Company */}
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-700">
                  {contact.contact_type === 'customer' ? contact.customer_name : contact.vendor_name}
                </span>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <a href={`mailto:${contact.primary_email || contact.email}`} className="text-sm text-blue-600 hover:underline">
                    {contact.primary_email || contact.email}
                  </a>
                </div>
                {(contact.client_direct_phone || contact.mobile_phone || contact.phone_number) && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-700">
                      {contact.client_direct_phone 
                        ? `${contact.client_direct_phone.countryCode} ${contact.client_direct_phone.number}`
                        : contact.mobile_phone
                        ? `${contact.mobile_phone.countryCode} ${contact.mobile_phone.number}`
                        : contact.phone_number}
                    </span>
                  </div>
                )}
                {contact.status && (
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      contact.status === 'Active' ? 'bg-green-100 text-green-700' :
                      contact.status === 'Passive' ? 'bg-yellow-100 text-yellow-700' :
                      contact.status === 'DNC' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {contact.status}
                    </span>
                  </div>
                )}
                {(contact.address || contact.city) && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                    <span className="text-sm text-gray-700">
                      {contact.address || `${contact.city || ''}${contact.state ? ', ' + contact.state : ''}`}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingContact(contact);
                    setShowEditModal(true);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  data-testid={`edit-contact-recruiter-${contact.id}`}
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Pagination */}
      {filteredContacts.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredContacts.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      )}

      {/* Add Contact Modal */}
      {showAddModal && (
        <AddContactModalComprehensive
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchContacts();
          }}
          customers={customers}
          vendors={vendors}
          backendUrl={backendUrl}
        />
      )}

      {/* Contact Detail Modal */}
      <ContactDetailModal
        contact={selectedContact}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedContact(null);
        }}
        canEdit={selectedContact ? isMyContact(selectedContact) : false}
        onEdit={(contact) => {
          setShowDetailModal(false);
          setEditingContact(contact);
          setShowEditModal(true);
        }}
      />

      {/* Edit Contact Modal */}
      {showEditModal && editingContact && (
        <EditContactModal
          contact={editingContact}
          onClose={() => {
            setShowEditModal(false);
            setEditingContact(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setEditingContact(null);
            fetchContacts(); // Refresh the list
            toast.success('Contact updated successfully!');
          }}
          customers={customers}
          vendors={vendors}
        />
      )}
    </div>
  );
};

export default ContactsManagement;
